import type { FreeArea } from "@rubyplay/effw-connector";

const DEFAULT_TOOLTIP_POSITION = "right";
const DEFAULT_FREE_AREA_SIZE = 240;
// Backend absolute positions preserve the legacy authoring units used before
// widget configuration moved out of EF. Slot v1 consumes the scaled coordinates.
const SLOT_V1_POSITION_SCALE = {
	desktop: 2,
	mobile: 6,
} as const;

export type WidgetArea = Omit<FreeArea, "id"> & {
	id?: string;
};

export type WidgetConfig = {
	areas: WidgetArea[] | null;
};

const DEFAULT_AREA_CONFIGS: FreeArea[] = [
	{
		id: "default-desktop-landscape",
		platform: "desktop",
		orientation: "landscape",
		absolute: { x: 50, y: 400 },
		relative: {
			offset: { left: 60 },
			alignX: "left",
			alignY: "center",
		},
		tooltipPosition: DEFAULT_TOOLTIP_POSITION,
		width: DEFAULT_FREE_AREA_SIZE,
		height: DEFAULT_FREE_AREA_SIZE,
	},
	{
		id: "default-desktop-portrait",
		platform: "desktop",
		orientation: "portrait",
		absolute: { x: 50, y: 400 },
		relative: {
			offset: { left: 60 },
			alignX: "left",
			alignY: "center",
		},
		tooltipPosition: DEFAULT_TOOLTIP_POSITION,
		width: DEFAULT_FREE_AREA_SIZE,
		height: DEFAULT_FREE_AREA_SIZE,
	},
	{
		id: "default-mobile-portrait",
		platform: "mobile",
		orientation: "portrait",
		absolute: { x: 50, y: 150 },
		relative: {
			offset: { left: 60 },
			alignX: "left",
			alignY: "center",
		},
		tooltipPosition: DEFAULT_TOOLTIP_POSITION,
		width: DEFAULT_FREE_AREA_SIZE,
		height: DEFAULT_FREE_AREA_SIZE,
	},
	{
		id: "default-mobile-landscape",
		platform: "mobile",
		orientation: "landscape",
		absolute: { x: 50, y: 200 },
		relative: {
			offset: { left: 60 },
			alignX: "left",
			alignY: "center",
		},
		tooltipPosition: DEFAULT_TOOLTIP_POSITION,
		width: DEFAULT_FREE_AREA_SIZE,
		height: DEFAULT_FREE_AREA_SIZE,
	},
];

function applyHomeButtonConfig(
	area: FreeArea,
	isHomeButtonEnabled: boolean
): FreeArea {
	if (!isHomeButtonEnabled || !area.withHomeButton) {
		return area;
	}

	const { absolute, relative, tooltipPosition, bubbleAnimDirection } =
		area.withHomeButton;

	return {
		...area,
		absolute: absolute ?? (relative ? undefined : area.absolute),
		relative: relative ?? (absolute ? undefined : area.relative),
		tooltipPosition: tooltipPosition ?? area.tooltipPosition,
		bubbleAnimDirection: bubbleAnimDirection ?? area.bubbleAnimDirection,
	};
}

function scaleAbsolutePlacement(
	absolute: FreeArea["absolute"],
	scale: number
): FreeArea["absolute"] {
	if (!absolute) {
		return undefined;
	}

	return {
		...(absolute.x === undefined ? {} : { x: absolute.x * scale }),
		...(absolute.y === undefined ? {} : { y: absolute.y * scale }),
	};
}

function scaleSlotAbsolutePositions(area: FreeArea): FreeArea {
	const scale = SLOT_V1_POSITION_SCALE[area.platform];

	return {
		...area,
		...(area.absolute
			? { absolute: scaleAbsolutePlacement(area.absolute, scale) }
			: {}),
		...(area.withHomeButton
			? {
				withHomeButton: {
					...area.withHomeButton,
					...(area.withHomeButton.absolute
						? {
							absolute: scaleAbsolutePlacement(
								area.withHomeButton.absolute,
								scale
							),
						}
						: {}),
				},
			}
			: {}),
	};
}

function toFreeArea(
	area: WidgetArea,
	index: number,
	isHomeButtonEnabled: boolean
): FreeArea {
	const configuredArea = applyHomeButtonConfig(
		{
			...area,
			id: area.id ?? `widget-${area.platform}-${area.orientation}-${index}`,
			tooltipPosition: area.tooltipPosition ?? DEFAULT_TOOLTIP_POSITION,
			width: area.width ?? DEFAULT_FREE_AREA_SIZE,
			height: area.height ?? DEFAULT_FREE_AREA_SIZE,
		},
		isHomeButtonEnabled
	);

	return scaleSlotAbsolutePositions(configuredArea);
}

export function getDefaultFreeAreas(): FreeArea[] {
	return DEFAULT_AREA_CONFIGS.map((area) =>
		scaleSlotAbsolutePositions({ ...area })
	);
}

export function resolveFreeAreas(
	widgetConfig: WidgetConfig | null | undefined,
	isHomeButtonEnabled: boolean
): FreeArea[] {
	const widgetAreas = widgetConfig?.areas ?? [];

	if (widgetAreas.length === 0) {
		return getDefaultFreeAreas();
	}

	return widgetAreas.map((area, index) =>
		toFreeArea(area, index, isHomeButtonEnabled)
	);
}
